/*
 * Inline confirmation for the newsletter signup, so submitting does not leave the site.
 *
 * WHY A FETCH IS POSSIBLE. proc.php answers with Access-Control-Allow-Origin: * when the request
 * carries jsonp=true, so an ordinary cross-origin fetch can read the body. It comes back as a
 * JSONP call rather than JSON:
 *
 *   _show_thank_you("13", "Thanks for signing up!", "https://prism.app-us1.com?...", "a@b.com")
 *
 * We never execute it. The callback name is enough to know ActiveCampaign accepted the address.
 * The message shown is ours, not AC's, because AC's says "Thanks for signing up!" and says nothing
 * about the confirmation click that double opt-in requires, which is the one instruction that
 * decides whether a signup is worth anything.
 *
 * WHY NOT AC'S OWN EMBED SCRIPT. /f/embed.php?id=13 does all of this already, but it is 46KB of
 * third-party JavaScript that renders its own markup and styling, which would replace the banner's
 * design, and the theme does not load external scripts.
 *
 * PROGRESSIVE ENHANCEMENT. Without this file the form is an ordinary POST and still subscribes.
 * The visitor simply lands on whatever the AC form's On Submit is set to, so that setting is the
 * no-JS fallback rather than the normal path.
 */

class NewsletterAc extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-newsletter-status]');
    if (!this.form || !this.status) return;

    /* Only take over an ActiveCampaign post. The Shopify customer form is a server round trip and
       keeps its own error and success handling. */
    if (this.form.action.indexOf('/proc.php') === -1) return;

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    const input = this.form.querySelector('input[type="email"]');
    if (!input || !input.checkValidity()) return; // let the browser show its own message

    event.preventDefault();
    this.setBusy(true);

    const params = new URLSearchParams(new FormData(this.form));
    params.set('jsonp', 'true');
    const url = this.form.action + '?' + params.toString();

    try {
      const response = await fetch(url, { method: 'GET', mode: 'cors' });
      const body = await response.text();
      this.render(response.ok && body.indexOf('_show_thank_you') !== -1);
    } catch (e) {
      /* Network failure, or a blocker in front of the request. The submission may or may not have
         landed, so do not claim success. */
      this.render(false);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    const button = this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = busy;
    this.form.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  render(ok) {
    this.status.hidden = false;
    this.status.textContent = ok
      ? this.status.dataset.successMessage
      : this.status.dataset.errorMessage;
    this.status.classList.toggle('newsletter-form__message--success', ok);
    if (ok) this.form.hidden = true;
    this.status.focus();
  }
}

if (!customElements.get('newsletter-ac')) {
  customElements.define('newsletter-ac', NewsletterAc);
}
